import { useEffect } from 'preact/hooks';
import type { StatusLedMapping, StatusLedWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { formatLeafValue } from '../../format/leafValue';
import './StatusLedRenderer.css';

interface Props {
  widget: StatusLedWidget;
  source: IPointBindingSource;
}

/**
 * Single dot whose fill and label are picked from an enum → colour map.
 *
 * Unmapped values keep a hollow dot, but the text is no longer the raw code:
 * when the bound leaf carries enum texts (Siemens OZW, Ecoforest ...), the
 * label comes from there, so "3" reads "Komfort" without the author copying
 * the controller's enum table into the mapping. Strings and booleans fall
 * back to their own text (`An`/`Aus` for booleans). The author's mapping
 * always wins.
 *
 * Layout: caption (widget label) on top, state below — a tile, not a pill,
 * so it lines up with gauges and counters on the same screen.
 */
export function StatusLedRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.statePoint);
  useEffect(() => release, [release]);
  const meta = useLeafMeta(widget.statePoint);

  const current = value.value;
  const stale = isDegraded(current);
  const hit = findMapping(widget.mapping, current?.value);

  const dotColour = hit?.colour ?? 'transparent';
  const labelText = hit?.label ?? formatLeafValue(current?.value, meta);

  return (
    <div class={`hv-led${stale ? ' hv-led--stale' : ''}`}>
      {widget.label && <span class="hv-led__caption">{widget.label}</span>}
      <span class="hv-led__state">
        <span class="hv-led__dot" style={{ background: dotColour, borderColor: hit?.colour ?? 'currentColor' }} />
        {(widget.showLabel ?? true) && (
          <span class="hv-led__label">{labelText}</span>
        )}
      </span>
    </div>
  );
}

/** Mapping row for a value. Also used by the value tile, which shares the
 *  mapping shape. Number and text count as equal when they read the same:
 *  drivers deliver state codes as 3 or as "3". */
export function findMapping(
  mapping: readonly StatusLedMapping[] | null | undefined,
  value: unknown,
): StatusLedMapping | undefined {
  if (!mapping || value === null || value === undefined) return undefined;
  return mapping.find((m) => valuesEqual(m.value, value));
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a) === String(b);
}
